import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { checkPermission } from 'utils/utils';
import {
  getDefaultRouteForPanel,
  getPanelModeFromPathname,
  PanelMode,
  readStoredPanelMode,
  writeStoredPanelMode,
} from 'utils/panelRoutes';

type PanelModeContextValue = {
  panelMode: PanelMode;
  setPanelMode: (mode: PanelMode) => void;
  /** User toggled panel — updates mode + persists; returns default route to navigate to. */
  switchPanel: (mode: PanelMode) => string;
  canAccessMedicinePanel: boolean;
  canAccessGroceryPanel: boolean;
  showPanelToggle: boolean;
};

const PanelModeContext = createContext<PanelModeContextValue | null>(null);

const resolveDefaultPanel = (
  canMedicine: boolean,
  canGrocery: boolean,
): PanelMode => {
  const stored = readStoredPanelMode();
  if (stored === 'medicine' && canMedicine) return 'medicine';
  if (stored === 'grocery' && canGrocery) return 'grocery';
  if (canMedicine) return 'medicine';
  if (canGrocery) return 'grocery';
  return 'medicine';
};

export const PanelModeProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const permissions = useSelector(
    (state: any) => state?.userProfile?.data?.permissions,
  );

  const canAccessMedicinePanel = useMemo(
    () =>
      checkPermission(permissions, {
        permissionName: 'Dashboard',
        permission: ['view', 'viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'Medicines',
        permission: ['view', 'viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'QualityCheck',
        permission: ['view', 'viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'Rejected',
        permission: ['view', 'viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'DraftMedicinesRequest',
        permission: ['view', 'viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'Categories',
        permission: ['viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'SubCategories',
        permission: ['viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'WarningTags',
        permission: ['viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'DoseForm',
        permission: ['viewAll'],
      }) ||
      checkPermission(permissions, {
        permissionName: 'CategoryDoseFormMapping',
        permission: ['viewAll'],
      }),
    [permissions],
  );

  const canAccessGroceryPanel = useMemo(
    () =>
      checkPermission(permissions, {
        permissionName: 'GroceryItems',
        permission: ['view', 'viewAll'],
      }),
    [permissions],
  );

  const [panelMode, setPanelModeState] = useState<PanelMode>(() => {
    const fromPath = getPanelModeFromPathname(pathname);
    if (fromPath) return fromPath;
    return readStoredPanelMode() ?? 'medicine';
  });

  // Keep context in sync with panel-exclusive routes before paint (sidebar + toggle).
  useLayoutEffect(() => {
    const fromPath = getPanelModeFromPathname(pathname);
    if (fromPath && panelMode !== fromPath) {
      setPanelModeState(fromPath);
      writeStoredPanelMode(fromPath);
    }
  }, [pathname, panelMode]);

  useEffect(() => {
    const fromPath = getPanelModeFromPathname(pathname);
    if (fromPath) {
      setPanelModeState(fromPath);
      writeStoredPanelMode(fromPath);
      return;
    }

    // Shared routes (e.g. /): do not reconcile until permissions are loaded.
    // Otherwise resolveDefaultPanel(false, false) overwrites stored grocery with medicine.
    if (!permissions) {
      return;
    }

    setPanelModeState((current) => {
      if (current === 'medicine' && !canAccessMedicinePanel && canAccessGroceryPanel) {
        return 'grocery';
      }
      if (current === 'grocery' && !canAccessGroceryPanel && canAccessMedicinePanel) {
        return 'medicine';
      }
      if (current === 'medicine' && canAccessMedicinePanel) return current;
      if (current === 'grocery' && canAccessGroceryPanel) return current;
      return resolveDefaultPanel(canAccessMedicinePanel, canAccessGroceryPanel);
    });
  }, [pathname, permissions, canAccessMedicinePanel, canAccessGroceryPanel]);

  const setPanelMode = useCallback((mode: PanelMode) => {
    setPanelModeState(mode);
    writeStoredPanelMode(mode);
  }, []);

  const switchPanel = useCallback((mode: PanelMode) => {
    setPanelModeState(mode);
    writeStoredPanelMode(mode);
    return getDefaultRouteForPanel(mode);
  }, []);

  const showPanelToggle =
    canAccessMedicinePanel && canAccessGroceryPanel;

  const value = useMemo(
    () => ({
      panelMode,
      setPanelMode,
      switchPanel,
      canAccessMedicinePanel,
      canAccessGroceryPanel,
      showPanelToggle,
    }),
    [
      panelMode,
      setPanelMode,
      switchPanel,
      canAccessMedicinePanel,
      canAccessGroceryPanel,
      showPanelToggle,
    ],
  );

  return (
    <PanelModeContext.Provider value={value}>
      {children}
    </PanelModeContext.Provider>
  );
};

export const usePanelMode = (): PanelModeContextValue => {
  const ctx = useContext(PanelModeContext);
  if (!ctx) {
    throw new Error('usePanelMode must be used within PanelModeProvider');
  }
  return ctx;
};
